import { useState } from "react";

import Color from "color";
import { Button, Modal, Table } from "semantic-ui-react";
import { styled } from "styled-components";

import { formatDate } from "simplydo/core/dates";
import { RichTextViewer } from "simplydo/react/richtext";

import { useAppSelector } from "store";

import { UserChip } from "components/lib/Chips";
import { Sortable } from "components/lib/dnd/Sortable";

import { EventProps, EventWithIndex } from "../calendar-types";

const EventStyleForeground = styled.div<{
  $startsToday?: boolean;
  $endsToday?: boolean;
  $color?: string;
  $dummy?: boolean;
  $selected?: boolean;
  $isOutsideRange?: boolean;
}>`
  flex: 1;
  cursor: pointer;
  color: #fff;
  font-weight: bold;

  position: relative;
  z-index: 1;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  font-size: 12px;
  opacity: ${({ $isOutsideRange }) => ($isOutsideRange ? 0.5 : 1)};

  background-color: ${({ $selected, $color }) =>
    $selected ? Color($color).darken(0.2).hex() : $color || "#2185d0"};
  padding: 0 6px;

  visibility: ${({ $dummy }) => ($dummy ? "hidden" : "visible")};

  ${({ $startsToday }) =>
    $startsToday ?
      `
      margin-left: 8px;
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    `
    : ""}

  ${({ $endsToday }) =>
    $endsToday ?
      `
      margin-right: 8px;
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    `
    : ""};
`;

type ForegroundEventProps = EventProps & {
  selected: boolean;
  index: number;
  onSelectEvent: (event: EventWithIndex | null) => void;
  deleteEvent: (eventId: string, eventType: string) => void;
  leaveEvent?: (eventId: string) => void;
  editable: boolean;
  isOutsideRange?: boolean;
};

export const ForegroundEvent = ({
  event,
  index,
  currDate,
  selected,
  onSelectEvent,
  deleteEvent,
  leaveEvent,
  setEditingEvent,
  editable,
  isOutsideRange,
}: ForegroundEventProps) => {
  const [open, setOpen] = useState(false);
  const user = useAppSelector((state) => state.user);

  if (event.eventType === "dummy") {
    return <EventStyleForeground $dummy>.</EventStyleForeground>;
  }

  const eventStartsToday =
    event.from.toDateString() === currDate.toDateString();
  const eventEndsToday = event.to?.toDateString() === currDate.toDateString();
  const eventIsMonday = currDate.getDay() === 1;
  const hasSeperateEndDate =
    event.to?.toDateString() !== event.from.toDateString();
  const dateFormat = "MMM dd, HH:mm";

  return (
    <>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={open}
        onClose={() => {
          setOpen(false);
          onSelectEvent(null);
        }}
      >
        <Modal.Header>{event.title}</Modal.Header>
        <Modal.Content>
          <h4>Date</h4>
          {hasSeperateEndDate ?
            `${formatDate(event.from, dateFormat)} - ${formatDate(event.to, dateFormat)}`
          : `${formatDate(event.from, dateFormat)}`}
          <h4>Description</h4>
          {event.description && (
            <RichTextViewer value={event.description || ""} />
          )}
          {event?.ownerMembers && event.ownerMembers.length > 0 ?
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Members</Table.HeaderCell>
                  <Table.HeaderCell></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {event.ownerMembers.map((member) => (
                  <Table.Row key={member._id}>
                    <Table.Cell>
                      <UserChip user={member} compact />
                    </Table.Cell>
                    <Table.Cell collapsing>
                      {leaveEvent && member._id === user._id ?
                        <Button
                          content="Leave"
                          icon="sign-out"
                          basic
                          onClick={() => leaveEvent(event.id)}
                        />
                      : null}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          : null}
        </Modal.Content>
        <Modal.Actions>
          {!editable ? null : (
            <>
              {event.id ?
                <Button
                  icon="trash"
                  basic
                  onClick={() => {
                    if (deleteEvent) {
                      deleteEvent(event.id, event.type);
                    }
                    setOpen(false);
                  }}
                />
              : null}
              <Button
                content="Edit"
                icon="edit"
                onClick={() => {
                  setOpen(false);
                  setEditingEvent(event, true);
                }}
              />
            </>
          )}
          <Button color="black" onClick={() => setOpen(false)}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
      <Sortable
        disabled={event.isGenerated}
        id={`${event.id}+${event.daysFromStart}`}
        index={index}
        attachToChild
        type="event"
        collisionPriority="Low"
        accept={[]}
        style={{ transform: null }}
      >
        <EventStyleForeground
          $startsToday={eventStartsToday}
          $endsToday={eventEndsToday}
          $selected={selected}
          $color={event.color}
          $isOutsideRange={isOutsideRange}
          onClick={() => {
            setOpen(true);
            onSelectEvent(event);
          }}
        >
          {eventStartsToday || eventIsMonday ?
            `${event.title}`
          : <span>&nbsp;</span>}
        </EventStyleForeground>
      </Sortable>
    </>
  );
};
